import { compact, uniq, without } from 'lodash';

import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { newNodeId } from '../../diagram/nodes';
import { TeamSettings } from '../../utils/team';

// This method tries to get the color of the node based on the node name in the following way:
//   1. It tries to find a color that has a keyword that is included in the node name - if found, that color is returned
//   2. If no such color is found, it tries to find the default color - if found, that color is returned
//   3. Fallback to #666 (should never happen because there should always be a default color)
export function getColorFromNodeName(
  nodeName: string,
  teamSettings: TeamSettings
): string {
  const lowercaseName = nodeName.toLowerCase();
  const colors = teamSettings.brand?.colors ?? [];
  return (
    colors.find((color) =>
      color.keywords.some((keyword) => lowercaseName.includes(keyword))
    )?.value ??
    colors.find((color) => color.isDefault)?.value ??
    '#666'
  );
}

export const buildSankeyFormNode = (
  name: string,
  teamSettings: TeamSettings
): SankeyFormNode => ({
  id: newNodeId(name),
  name,
  color: getColorFromNodeName(name, teamSettings),
  use_custom_amount_label: false,
  custom_amount_label: '',
  use_custom_comparison_label: false,
  custom_comparison_label: '',
});

// Gets the list of unique node names from the flows, ordered by their appearance
export const getNodeNamesFromFlows = (flows: SankeyFormFlow[]): string[] =>
  uniq(compact(flows.flatMap(({ source, target }) => [source, target])));

export const patchNodesWithNewFlows = (
  previousNodes: SankeyFormNode[],
  newFlows: SankeyFormFlow[],
  onRenameNode: (oldName: string, newName: string) => void,
  teamSettings: TeamSettings
): SankeyFormNode[] => {
  // Unique node names appearing in the new flows
  const newNodeNames = getNodeNamesFromFlows(newFlows);

  // Nodes from the previous list that are not appearing in the new flows anymore
  const removedNodes = previousNodes.filter(
    (node) => !newNodeNames.includes(node.name ?? '')
  );

  // Nodes from the previous list that are still present in the new flows
  const preservedNodes = previousNodes.filter((node) =>
    newNodeNames.includes(node.name ?? '')
  );

  // Node names that are in the new flows but not in the previous nodes list
  const addedNodesNames = without(
    newNodeNames,
    ...previousNodes.map((node) => node.name ?? '')
  );

  // Case 1. If this is the first call, we just initialize all the nodes
  if (previousNodes.length === 0) {
    return newNodeNames.map((name) => buildSankeyFormNode(name, teamSettings));
  }

  // Case 2. If we have exactly one removed and one added node, we assume that they are the same node that just got renamed
  if (removedNodes.length === 1 && addedNodesNames.length === 1) {
    onRenameNode(removedNodes[0].name ?? '', addedNodesNames[0]);
    const renamedNode = { ...removedNodes[0], name: addedNodesNames[0] };
    return [...preservedNodes, renamedNode];
  }

  // Case 3. Otherwise we just add new nodes from the table data to the list
  return [
    ...preservedNodes,
    ...addedNodesNames.map((name) => buildSankeyFormNode(name, teamSettings)),
  ];
};

export function useIsDataFromExcel() {
  const { form } = useSankeyFormContext();
  return form.watch('data_origin') === 'excel';
}
